import { useState } from 'react'
import type { ReactNode } from 'react'
import {
  Copy,
  ExternalLink,
  LineChart,
  Link2,
  Megaphone,
  Play,
  Plus,
  Save,
  Settings2,
  Trash2,
  Users,
} from 'lucide-react'
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  ProductCoverPlaceholder,
  SelectField,
  Skeleton,
  StatusBadge,
  TextField,
} from '@shared/design-system/ui'
import { affiliateCheckoutUrl } from '@shared/config/public-urls'
import { pixelIdFormatError, pixelTrackingReady } from '@shared/contracts/pixels'
import { PixelEventsTimeline } from '@shared/ui/PixelEventsTimeline'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import {
  affiliationStatusLabel,
  percentageLabel,
  useAffiliatePixelsQuery,
  useMarketplaceProductsQuery,
  useMyAffiliationsQuery,
} from '@entities/partnership'
import type { Affiliation, AffiliatePixel, AffiliatePixelProvider } from '@entities/partnership'
import { toast } from '@shared/lib/toast/toast-store'
import { useAffiliatePixelsMutation } from '../api/affiliate.mutations'
import { affiliationStatusTone } from '../model/affiliation-status'
import { CommissionScopePanel } from './CommissionScopePanel'
import {
  affiliationCard,
  affiliationCover,
  affiliationDetail,
  affiliationHeader,
  affiliationInfo,
  affiliationMeta,
  affiliationName,
  affiliationSummary,
  linkList,
  list,
  promotionLinkInfo,
  promotionLinkLabel,
  promotionLinkRow,
  promotionLinkValue,
  pixelActions,
  pixelCard,
  pixelCommissionNotice,
  pixelFormGrid,
  pixelProviderBar,
  pixelPanel,
  pixelPanelHeader,
  pixelToggleRow,
} from './affiliates.css'

const affiliatePixelProviders = ['facebook', 'tiktok', 'google_ads', 'utmify'] as const

const affiliatePixelProviderMeta = {
  facebook: {
    title: 'Meta',
    icon: <Megaphone size={15} />,
  },
  tiktok: {
    title: 'TikTok',
    icon: <Play size={15} />,
  },
  google_ads: {
    title: 'Google Ads',
    icon: <LineChart size={15} />,
  },
  utmify: {
    title: 'UTMify',
    icon: <Link2 size={15} />,
  },
} satisfies Record<
  AffiliatePixelProvider,
  {
    title: string
    icon: ReactNode
  }
>

const affiliateGoogleConversionEventOptions = [
  { value: 'purchase', label: 'Compra confirmada' },
  { value: 'begin_checkout', label: 'Visita ao checkout' },
]

function unavailableReason(status: Affiliation['status']) {
  if (status === 'PENDENTE') return 'Links liberados após aprovação do produtor.'
  if (status === 'RECUSADO') return 'Afiliação recusada pelo produtor.'
  return 'Afiliação bloqueada para divulgação.'
}

async function copyPromotionLink(value: string) {
  if (!navigator.clipboard) {
    toast.error({
      title: 'Não foi possível copiar o link',
      description: 'Selecione e copie o link manualmente.',
    })
    return
  }
  await navigator.clipboard.writeText(value)
  toast.success('Link de divulgação copiado.')
}

function makePixelId(provider: AffiliatePixelProvider) {
  const suffix =
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}_${Math.round(Math.random() * 100_000)}`
  return `local_${provider}_${suffix}`
}

function newAffiliatePixel(provider: AffiliatePixelProvider): AffiliatePixel {
  const id = makePixelId(provider)
  if (provider === 'facebook' || provider === 'tiktok') {
    return { id, provider, pixelId: '', accessToken: '', sendOnlyCommission: true }
  }
  if (provider === 'google_ads') {
    return {
      id,
      provider,
      pixelId: '',
      conversionLabel: '',
      conversionEvent: 'purchase',
      sendOnlyCommission: true,
    }
  }
  return { id, provider, accessToken: '', sendOnlyCommission: true }
}

function AffiliatePixelsPanel({ agreement }: { agreement: Affiliation }) {
  const [open, setOpen] = useState(false)
  const pixels = useAffiliatePixelsQuery(open ? agreement.id : undefined)
  const savePixels = useAffiliatePixelsMutation(agreement.id)
  const [draft, setDraft] = useState<AffiliatePixel[]>([])
  const [loadedPixels, setLoadedPixels] = useState<AffiliatePixel[] | undefined>(undefined)

  if (pixels.data && pixels.data !== loadedPixels) {
    setLoadedPixels(pixels.data)
    setDraft(pixels.data)
  }

  function addPixel(provider: AffiliatePixelProvider) {
    const providerPixels = draft.filter((pixel) => pixel.provider === provider)
    if (providerPixels.length >= 10) {
      toast.error({
        title: 'Limite de pixels atingido',
        description: 'Cada provedor pode ter até 10 pixels nesta afiliação.',
      })
      return
    }
    if (providerPixels.some((pixel) => !pixelTrackingReady(pixel))) {
      toast.info({
        title: 'Complete o pixel antes de adicionar outro',
        description: `Preencha o pixel ${affiliatePixelProviderMeta[provider].title} incompleto ou remova-o.`,
      })
      return
    }
    setDraft((current) => [...current, newAffiliatePixel(provider)])
  }

  function replacePixel(next: AffiliatePixel) {
    setDraft((current) => current.map((pixel) => (pixel.id === next.id ? next : pixel)))
  }

  function removePixel(pixelId: string) {
    setDraft((current) => current.filter((pixel) => pixel.id !== pixelId))
  }

  function save() {
    const incomplete = draft.find((pixel) => !pixelTrackingReady(pixel))
    if (incomplete) {
      toast.info(
        `Complete o pixel ${affiliatePixelProviderMeta[incomplete.provider].title} antes de salvar.`,
      )
      return
    }
    savePixels.mutate(draft, {
      onSuccess: () => toast.success('Pixels da afiliação salvos.'),
      onError: (error) =>
        toast.error(
          getUserFacingError(error, {
            title: 'Não foi possível salvar os pixels',
            fallback: 'Revise os campos destacados e tente novamente.',
          }),
        ),
    })
  }

  return (
    <div className={pixelPanel}>
      <div className={pixelPanelHeader}>
        <div>
          <span className={promotionLinkLabel}>Pixels da sua divulgação</span>
          <p className={affiliationMeta}>
            Eventos próprios do afiliado, enviados junto com essa divulgação.
          </p>
          <p className={pixelCommissionNotice}>
            O Purchase dos seus pixels sai pelo valor da sua comissão.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Settings2 size={15} />}
          onClick={() => setOpen((current) => !current)}
        >
          {open ? 'Fechar' : 'Configurar'}
        </Button>
      </div>

      {open ? (
        <>
          {pixels.isPending ? (
            <Skeleton width="100%" height={72} />
          ) : pixels.isError ? (
            <ErrorState
              onRetry={() => {
                void pixels.refetch()
              }}
            />
          ) : (
            <>
              <div className={pixelProviderBar}>
                {affiliatePixelProviders.map((provider) => {
                  const meta = affiliatePixelProviderMeta[provider]
                  return (
                    <Button
                      key={provider}
                      variant="secondary"
                      size="sm"
                      leftIcon={<Plus size={14} />}
                      onClick={() => addPixel(provider)}
                    >
                      {meta.title}
                    </Button>
                  )
                })}
              </div>

              {draft.length === 0 ? (
                <span className={affiliationMeta}>Nenhum pixel configurado nesta afiliação.</span>
              ) : (
                <div className={linkList}>
                  {draft.map((pixel, index) => {
                    const meta = affiliatePixelProviderMeta[pixel.provider]
                    const ready = pixelTrackingReady(pixel)
                    return (
                      <div key={pixel.id} className={pixelCard}>
                        <div className={pixelPanelHeader}>
                          <div>
                            <span className={promotionLinkLabel}>
                              {meta.icon} {meta.title} {index + 1}
                            </span>
                            <p className={affiliationMeta}>
                              {ready ? 'Pronto para disparo.' : 'Complete os campos obrigatórios.'}
                            </p>
                          </div>
                          <Button
                            variant="text"
                            size="sm"
                            aria-label={`Remover pixel ${meta.title}`}
                            onClick={() => removePixel(pixel.id)}
                          >
                            <Trash2 size={15} />
                          </Button>
                        </div>

                        <PixelEventsTimeline
                          provider={pixel.provider}
                          googleBeginCheckout={
                            pixel.provider === 'google_ads' &&
                            pixel.conversionEvent === 'begin_checkout'
                          }
                        />

                        {pixel.provider === 'facebook' || pixel.provider === 'tiktok' ? (
                          <div className={pixelFormGrid}>
                            <TextField
                              label="Pixel ID"
                              value={pixel.pixelId}
                              placeholder={pixel.provider === 'tiktok' ? 'C12345...' : '1234567890'}
                              error={pixelIdFormatError(pixel)}
                              onChange={(event) =>
                                replacePixel({ ...pixel, pixelId: event.target.value.trim() })
                              }
                            />
                            <TextField
                              label="Token de acesso"
                              type="password"
                              autoComplete="new-password"
                              value={pixel.accessToken}
                              placeholder="Opcional"
                              helperText="Opcional. Cole o token gerado no painel do provedor para ativar todos os eventos disponíveis."
                              onChange={(event) =>
                                replacePixel({ ...pixel, accessToken: event.target.value.trim() })
                              }
                            />
                          </div>
                        ) : pixel.provider === 'google_ads' ? (
                          <div className={pixelFormGrid}>
                            <TextField
                              label="Conversion ID"
                              value={pixel.pixelId}
                              placeholder="AW-123456789"
                              error={pixelIdFormatError(pixel)}
                              onChange={(event) =>
                                replacePixel({ ...pixel, pixelId: event.target.value.trim() })
                              }
                            />
                            <TextField
                              label="Rótulo de conversão"
                              value={pixel.conversionLabel}
                              placeholder="abcDEF123..."
                              onChange={(event) =>
                                replacePixel({
                                  ...pixel,
                                  conversionLabel: event.target.value.trim(),
                                })
                              }
                            />
                            <SelectField
                              label="Evento de conversão"
                              value={pixel.conversionEvent}
                              options={affiliateGoogleConversionEventOptions}
                              onChange={(event) =>
                                replacePixel({
                                  ...pixel,
                                  conversionEvent:
                                    event.target.value === 'begin_checkout'
                                      ? 'begin_checkout'
                                      : 'purchase',
                                })
                              }
                            />
                          </div>
                        ) : (
                          <div className={pixelFormGrid}>
                            <TextField
                              label="Token de acesso"
                              type="password"
                              autoComplete="new-password"
                              value={pixel.accessToken}
                              placeholder="Opcional"
                              helperText="Opcional. Cole o token gerado no painel da UTMify para manter os pedidos atualizados."
                              onChange={(event) =>
                                replacePixel({ ...pixel, accessToken: event.target.value.trim() })
                              }
                            />
                          </div>
                        )}

                        <div className={pixelToggleRow}>
                          <span className={affiliationMeta}>Enviar apenas comissão</span>
                          <StatusBadge tone="informative" label="Sempre ativo" size="sm" />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              <div className={pixelActions}>
                <Button
                  size="sm"
                  leftIcon={<Save size={15} />}
                  disabled={savePixels.isPending}
                  onClick={save}
                >
                  {savePixels.isPending ? 'Salvando...' : 'Salvar pixels'}
                </Button>
              </div>
            </>
          )}
        </>
      ) : null}
    </div>
  )
}

export function MyAffiliationsPanel() {
  const mine = useMyAffiliationsQuery()
  const marketplace = useMarketplaceProductsQuery()

  const nameByProduct = new Map(
    (marketplace.data ?? []).map((product) => [product.id, product.name]),
  )

  if (mine.isPending) {
    return (
      <div className={list}>
        {[0, 1, 2].map((index) => (
          <Card key={index} padding="md">
            <Skeleton width="40%" height={16} />
            <div style={{ height: 8 }} />
            <Skeleton width="25%" height={12} />
          </Card>
        ))}
      </div>
    )
  }

  if (mine.isError) {
    return (
      <ErrorState
        onRetry={() => {
          void mine.refetch()
        }}
      />
    )
  }

  if (mine.data.length === 0) {
    return (
      <EmptyState
        icon={<Users size={22} />}
        title="Você ainda não é afiliado"
        description="Escolha um produto no marketplace ou use um link de convite para começar a divulgar."
      />
    )
  }

  return (
    <div className={list}>
      {mine.data.map((agreement) => {
        const productName =
          agreement.product?.name ?? nameByProduct.get(agreement.productId) ?? agreement.productId
        const promotionLinks = agreement.product?.promotionLinks ?? []
        return (
          <Card key={agreement.id} padding="md" className={affiliationCard}>
            <div className={affiliationHeader}>
              <div className={affiliationSummary}>
                {agreement.product?.coverImage ? (
                  <div
                    className={affiliationCover}
                    style={{ backgroundImage: `url(${agreement.product.coverImage})` }}
                    role="img"
                    aria-label={productName}
                  />
                ) : (
                  <div className={affiliationCover}>
                    <ProductCoverPlaceholder name={productName} />
                  </div>
                )}
                <div className={affiliationInfo}>
                  <span className={affiliationName}>{productName}</span>
                  <span className={affiliationMeta}>
                    Comissão de {percentageLabel(agreement.commissionPercentage)}
                  </span>
                </div>
              </div>
              <StatusBadge
                tone={affiliationStatusTone(agreement.status)}
                label={affiliationStatusLabel(agreement.status)}
                size="sm"
              />
            </div>

            <div className={affiliationDetail}>
              {agreement.status === 'APROVADO' ? (
                <>
                  {promotionLinks.length > 0 ? (
                    <div className={linkList}>
                      {promotionLinks.map((link) => {
                        const promotionUrl = affiliateCheckoutUrl(
                          link.checkoutId,
                          agreement.affiliateId,
                        )
                        return (
                          <div key={link.checkoutId} className={promotionLinkRow}>
                            <div className={promotionLinkInfo}>
                              <span className={promotionLinkLabel}>
                                {link.label || 'Link de venda'}
                              </span>
                              <span className={promotionLinkValue}>{promotionUrl}</span>
                            </div>
                            <Button
                              variant="secondary"
                              size="sm"
                              leftIcon={<Copy size={15} />}
                              onClick={() => {
                                void copyPromotionLink(promotionUrl)
                              }}
                            >
                              Copiar
                            </Button>
                            <Button
                              variant="text"
                              size="sm"
                              leftIcon={<ExternalLink size={15} />}
                              onClick={() => {
                                window.open(promotionUrl, '_blank', 'noopener,noreferrer')
                              }}
                            >
                              Abrir
                            </Button>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <span className={affiliationMeta}>Nenhum checkout ativo para venda.</span>
                  )}
                  <AffiliatePixelsPanel agreement={agreement} />
                </>
              ) : (
                <span className={affiliationMeta}>
                  <Link2 size={13} /> {unavailableReason(agreement.status)}
                </span>
              )}
              <CommissionScopePanel
                productId={agreement.productId}
                commissionPercentage={agreement.commissionPercentage}
              />
            </div>
          </Card>
        )
      })}
    </div>
  )
}
